import {
  FormControl,
  InputBase,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  IconButton,
  Box,
} from "@material-ui/core";
import React, { FC } from "react";
import { Floor } from "../../floor/floor.model";
import { makeStyles, withStyles, Theme } from "@material-ui/core/styles";
import { Favorite, FavoriteBorder } from "@material-ui/icons";

const useStyles = makeStyles((theme: Theme) => ({
  formControl: {
    marginLeft: theme.spacing(1),
    minWidth: 100,
    "& label": {
      backgroundColor: theme.palette.background.default,
      paddingInline: theme.spacing(0.5),
    },
  },
  menuItem: {
    fontSize: theme.typography.fontSize,
    paddingBlock: 4,
    paddingInline: 8,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  disabledMenuItem: {
    fontSize: theme.typography.fontSize,
    paddingBlock: 4,
    paddingInline: 8,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.text.disabled,
    textDecoration: "line-through",
  },
  invalidValue: {
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.text.disabled,
    textDecoration: "line-through",
  },
  favouriteButton: {
    padding: 4,
    marginLeft: theme.spacing(1),
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
  favouriteIcon: {
    fontSize: 18,
    color: theme.palette.secondary.main,
  },
}));

export const FilterInputSmallStyle = withStyles((theme: Theme) => ({
  input: {
    border: `1px solid ${theme.palette.grey[400]}`,
    borderRadius: 3,
    textTransform: "capitalize",
    color: theme.palette.text.secondary,
    padding: "3px",
    "&:focus": {
      backgroundColor: "inherit",
      borderRadius: 3,
    },
  },
}))(InputBase);

export const FilterInputStyle = withStyles((theme: Theme) => ({
  input: {
    border: `1px solid ${theme.palette.grey[400]}`,
    borderRadius: 0,
    textTransform: "capitalize",
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
    padding: 7,
    "&:focus": {
      backgroundColor: "inherit",
    },
  },
}))(InputBase);

type Props = {
  floors: Floor[];
  validFloorIds?: Set<string>;
  defaultValue?: Floor;
  smallSize?: boolean;
  hideLabel?: boolean;
  showAll?: boolean;
  onChange: (floor: Floor) => void;
  enableAll?: boolean;
  onFavourite?: (floorId: string) => void;
  favouriteFloorId?: string;
};

const FloorSelect: FC<Props> = ({
  floors,
  validFloorIds,
  defaultValue,
  smallSize,
  hideLabel,
  showAll,
  onChange,
  enableAll,
  onFavourite,
  favouriteFloorId,
}) => {
  const classes = useStyles();

  const handleChange = (event: React.ChangeEvent<any>) => {
    onChange(event.target.value);
  };

  const handleFavouriteClick = (event: React.MouseEvent, floorId: string) => {
    event.stopPropagation();
    if (onFavourite) {
      onFavourite(floorId);
    }
  };

  const getDefaultValue = () => {
    return defaultValue
      ? (defaultValue && floors.find((t) => t.id === defaultValue.id)) || ""
      : showAll
        ? ""
        : null;
  };

  const renderValue = (selected: any) => {
    if (!selected) {
      return showAll ? "Todos" : "";
    }
    const isValid = !validFloorIds || validFloorIds.has(selected.id);
    return (
      <span className={isValid ? "" : classes.invalidValue}>
        {selected.name || ""}
      </span>
    );
  };

  const sortedFloors = enableAll
    ? floors
    : [...floors].sort((a, b) => {
        const aHasImage = !!a.image;
        const bHasImage = !!b.image;
        if (aHasImage !== bHasImage) return aHasImage ? -1 : 1;
        if (aHasImage && bHasImage)
          return (a.order ?? Infinity) - (b.order ?? Infinity);
        return 0;
      });

  return (
    <FormControl variant="outlined" className={classes.formControl}>
      {!hideLabel && (
        <InputLabel shrink htmlFor="floor-select">
          Piso
        </InputLabel>
      )}
      <Select
        id="floor-select"
        labelId="floor-select"
        label="Piso"
        displayEmpty={showAll}
        value={getDefaultValue()}
        onChange={handleChange}
        renderValue={renderValue}
        input={smallSize ? <FilterInputSmallStyle /> : <FilterInputStyle />}
      >
        {showAll && (
          <MenuItem key="empty" value={""} className={classes.menuItem}>
            Todos
          </MenuItem>
        )}
        {sortedFloors.map((t: any, index) => {
          const isValid = !validFloorIds || validFloorIds.has(t.id);
          const menuItemClass = isValid
            ? classes.menuItem
            : classes.disabledMenuItem;

          return t.image || enableAll ? (
            <MenuItem
              key={`${t.id}-${index}`}
              value={t}
              className={menuItemClass}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                width="100%"
              >
                <span>{t.name}</span>
                {onFavourite && (
                  <IconButton
                    className={classes.favouriteButton}
                    size="small"
                    onClick={(e) => handleFavouriteClick(e, t.id)}
                  >
                    {favouriteFloorId === t.id ? (
                      <Favorite className={classes.favouriteIcon} />
                    ) : (
                      <FavoriteBorder className={classes.favouriteIcon} />
                    )}
                  </IconButton>
                )}
              </Box>
            </MenuItem>
          ) : (
            <div>
              <Tooltip title="El piso no posee plano">
                <div
                  style={{ display: "block" }}
                  onClick={(event) => event.stopPropagation()}
                >
                  <MenuItem
                    key={`${t.id}-${index}`}
                    disabled
                    value={t}
                    className={classes.menuItem}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      width="100%"
                    >
                      <span>{t.name}</span>
                      {onFavourite && (
                        <IconButton
                          className={classes.favouriteButton}
                          size="small"
                          disabled
                        >
                          <FavoriteBorder className={classes.favouriteIcon} />
                        </IconButton>
                      )}
                    </Box>
                  </MenuItem>
                </div>
              </Tooltip>
            </div>
          );
        })}
      </Select>
    </FormControl>
  );
};

export default FloorSelect;
